import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import './PrivateLessons.css';
import {
  API_BASE,
  authHeaders,
  formatDateTime,
  statusBadgeClass,
  statusLabel
} from './privateLessonUtils.js';

function MyPrivateLessons({ embedded = false, title = 'Mijn privelessen' }) {
  const [lessons, setLessons] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionId, setActionId] = useState('');

  const fetchLessons = async () => {
    try {
      setIsLoading(true);

      const res = await axios.get(`${API_BASE}/private-lesson/my`, {
        headers: authHeaders()
      });

      setLessons(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (err) {
      console.error('Error fetching my lessons', err);
      setLessons([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchLessons();
  }, []);

  const nowTime = Date.now();

  const upcomingLessons = useMemo(
    () =>
      lessons
        .filter((item) => item.start_at && new Date(item.start_at).getTime() >= nowTime)
        .sort((a, b) => new Date(a.start_at) - new Date(b.start_at)),
    [lessons, nowTime]
  );

  const pastLessons = useMemo(
    () =>
      lessons
        .filter((item) => item.start_at && new Date(item.start_at).getTime() < nowTime)
        .sort((a, b) => new Date(b.start_at) - new Date(a.start_at)),
    [lessons, nowTime]
  );

  const postAction = async (id, action, confirmText, body = {}) => {
    const confirmed = window.confirm(confirmText);
    if (!confirmed) return;

    try {
      setActionId(`${action}-${id}`);

      await axios.post(`${API_BASE}/private-lesson/${id}/${action}`, body, {
        headers: authHeaders()
      });

      fetchLessons();
    } catch (err) {
      console.error(`Error ${action} lesson`, err);
      alert(err?.response?.data?.error || 'Actie mislukt.');
    } finally {
      setActionId('');
    }
  };

  const cancelLesson = async (id) => {
    const reason = window.prompt('Reden van annuleren? Je mag dit leeg laten.');
    if (reason === null) return;

    try {
      setActionId(`cancel-${id}`);

      await axios.post(
        `${API_BASE}/private-lesson/${id}/cancel`,
        { reason: reason || null },
        { headers: authHeaders() }
      );

      fetchLessons();
    } catch (err) {
      console.error('Error cancelling lesson', err);
      alert(err?.response?.data?.error || 'Annuleren mislukt.');
    } finally {
      setActionId('');
    }
  };

  const canConfirm = (item) => String(item.status || '').toLowerCase() === 'requested';

  const canComplete = (item) =>
    ['confirmed', 'planned'].includes(String(item.status || '').toLowerCase());

  const canCancel = (item) =>
    ['requested', 'confirmed', 'planned'].includes(String(item.status || '').toLowerCase());

  const renderLessonsTable = (items, showActions = false) => {
    if (items.length === 0) {
      return <p>Geen privelessen gevonden.</p>;
    }

    return (
      <div className="table-responsive mb-4">
        <table className="table table-bordered align-middle">
          <thead>
            <tr>
              <th>Leerling</th>
              <th>Onderwerp</th>
              <th>Datum/tijd</th>
              <th>Duur</th>
              <th>Locatie</th>
              <th>Status</th>
              {showActions && <th>Acties</th>}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              <tr key={item.private_lesson_id}>
                <td>{item.student_name || '-'}</td>
                <td>{item.subject || 'Priveles'}</td>
                <td>{formatDateTime(item.start_at)}</td>
                <td>{item.duration_minutes || '-'} min</td>
                <td>{item.location_name || '-'}</td>
                <td>
                  <span className={statusBadgeClass(item.status)}>
                    {statusLabel(item.status)}
                  </span>
                </td>
                {showActions && (
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      {canConfirm(item) && (
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          disabled={actionId === `confirm-${item.private_lesson_id}`}
                          onClick={() =>
                            postAction(
                              item.private_lesson_id,
                              'confirm',
                              'Deze priveles bevestigen?'
                            )
                          }
                        >
                          Akkoord
                        </button>
                      )}

                      {canComplete(item) && (
                        <button
                          type="button"
                          className="btn btn-outline-success btn-sm"
                          disabled={actionId === `complete-${item.private_lesson_id}`}
                          onClick={() =>
                            postAction(
                              item.private_lesson_id,
                              'complete',
                              'Deze priveles als afgerond markeren?'
                            )
                          }
                        >
                          Afgerond
                        </button>
                      )}

                      {canComplete(item) && (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          disabled={actionId === `no-show-${item.private_lesson_id}`}
                          onClick={() =>
                            postAction(
                              item.private_lesson_id,
                              'no-show',
                              'Deze priveles als no-show markeren?'
                            )
                          }
                        >
                          No-show
                        </button>
                      )}

                      {canCancel(item) && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          disabled={actionId === `cancel-${item.private_lesson_id}`}
                          onClick={() => cancelLesson(item.private_lesson_id)}
                        >
                          Annuleren
                        </button>
                      )}

                      {!canConfirm(item) && !canComplete(item) && !canCancel(item) && (
                        <span className="text-muted">Geen acties</span>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const content = (
    <>
      {embedded ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <h4 className="private-home-title mb-0">{title}</h4>
          <span className="text-muted">{lessons.length} les(sen)</span>
        </div>
      ) : (
        <>
          <h2 className="text-center">{title}</h2>
          <hr className="seperator" />
        </>
      )}

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          <h4 className="mb-3">Komende privelessen</h4>
          {renderLessonsTable(upcomingLessons, true)}

          <h4 className="mb-3">Afgelopen privelessen</h4>
          {renderLessonsTable(pastLessons, false)}
        </>
      )}
    </>
  );

  if (embedded) {
    return <div>{content}</div>;
  }

  return <div className="bg-white p-4">{content}</div>;
}

export default MyPrivateLessons;
